import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import styled, { CSSProperties } from 'styled-components'
import { FaRegEye, FaRegEyeSlash } from 'react-icons/fa'
import useRegister from 'src/hooks/useRegister'
import RedButton from './RedButton'
import { blue } from '../styles'

const AccountContainer = styled.form`
  position: relative;
  display: grid;
  gap: 12px;
  padding: 24px 0 48px;
  max-width: 450px;
  margin: 0 16px;
`

const PasswordContainer = styled.div`
  position: relative;
  display: grid;
`

const Input = styled.input`
  font-family: 'Outfit';
  font-weight: 300;
  font-size: 1.25rem;
  padding: 12px 24px;
  border-radius: 24px;
  border: 1px solid ${blue};
`

const Disclaimer = styled.p`
  font-family: 'Outfit';
  color: grey;
`

const Errors = styled.div`
  display: grid;
  color: red;
  height: 60px;
  font-family: 'Outfit';
`

const eyeIconStyles = {
  position: 'absolute',
  justifySelf: 'end',
  alignSelf: 'center',
  right: '12px',
  cursor: 'pointer',
} as CSSProperties // idk why but this icon doesn't like position: 'absolute'
  
type RegistrationFormProps = {
  onSuccess?: () => any
  language?: 'japanese' | 'korean'
}

const RegistrationForm: React.FC<RegistrationFormProps> = ({ onSuccess, language = 'japanese' }) => {
  const [showPassword, setShowPassword] = useState(false)
  const [showResError, setShowResError] = useState(false)
  const { onSubmit, loading, res, error } = useRegister({ language })
  const { register, formState: { errors }, handleSubmit } = useForm()
  
  const formErrors = errors?.email || errors?.password
  
  useEffect(() => {
    if (error) setShowResError(true)
  }, [error])
  
  useEffect(() => {
    if (res?.success) onSuccess()
  }, [res])
  
  return (
    <AccountContainer onSubmit={handleSubmit(onSubmit)}>
      <Input
        type='email'
        placeholder='Email'
        {...register('email', {
          required: 'please enter your email',
          pattern: {
            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
            message: 'invalid email address',
          },
          onChange: () => setShowResError(false)
        })}
       />
      <PasswordContainer>
        <Input
          type={showPassword ? 'text' : 'password'}
          placeholder='Password'
          {...register('password', {
            required: 'please enter a password',
            minLength: {
              value: 8,
              message: 'password must be at least 8 characters',
            },
            pattern: {
              value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[~!@#$%^&*_\-+=`|(){}[\]:;"'<>,.?/])(?=.{8,})/,
              message: 'password must contain at least 1 uppercase letter, 1 lowercase letter, 1 number, and 1 special character',
            }
          })}
        />
        {showPassword
          ? <FaRegEyeSlash size={24} style={eyeIconStyles} onClick={() => setShowPassword(false)} />
          : <FaRegEye size={24} style={eyeIconStyles} onClick={() => setShowPassword(true)} />
        }
      </PasswordContainer>
      <Disclaimer>
        By creating an account, you agree to our{' '}
        <a href='/tos' target='_blank' rel='noopener noreferrer'>Terms of Use</a> and{' '}
        <a href='/privacy' target='_blank' rel='noopener noreferrer'>Privacy Policy</a>
      </Disclaimer>
      <Errors>
        {formErrors && (
          errors?.email
            ? <p>{errors.email.message}</p>
            : <p>{errors.password.message}</p>
        )}
        {error && showResError && <p>{error}</p>}
      </Errors>
      <RedButton
        type='submit'
        loading={loading}
        disabled={loading || res?.success}
        style={{ justifySelf: 'center' }}
      >
        Sign Up
      </RedButton>
    </AccountContainer>
  )
}

export default RegistrationForm
